import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { clientesApi } from "src/services/clientesApi";
import moment from "moment";
import { capitalizarPalabras } from "src/utils/sharedFunctions";
import { formatearRutDisplay } from "src/utils/conciliacionHelpers";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: "800px",
    maxWidth: "900px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  tableContainer: {
    maxHeight: 500,
  },
  accionChip: {
    fontWeight: 500,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const ModalHistorialAuditoriaClaves = ({
  open,
  onClose,
  refCliente,
  institucion,
  usuario,
  refSucursal,
}) => {
  const classes = useStyles();

  // Consumir endpoint de historial
  const { data: historial = [], isLoading } =
    clientesApi.useObtenerHistorialClaveQuery(
      {
        refCliente,
        institucion,
        usuario,
        refSucursal,
      },
      {
        skip: !open, // Solo cargar cuando el modal esté abierto
      },
    );

  // Configuración de colores según acción
  const getAccionConfig = (accion) => {
    const configs = {
      VISUALIZACION: { color: "primary", label: "Visualización" },
      COPIA: { color: "default", label: "Copia" },
      CREACION: { color: "secondary", label: "Creación" },
      EDICION: { color: "default", label: "Edición" },
      ELIMINACION: { color: "secondary", label: "Eliminación" },
    };
    return configs[accion] || { color: "default", label: accion };
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    return moment(fecha).format("DD/MM/YYYY HH:mm:ss");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        <Typography variant="h6">Historial de Auditoría</Typography>
        <Typography variant="body2" color="textSecondary">
          Usuario: <strong>{usuario}</strong> | Institución:{" "}
          <strong>{institucion}</strong>
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : historial.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body1">
              No hay registros de auditoría para esta clave.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Fecha y Hora</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Empleado</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Rol</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Acción</strong>
                  </TableCell>
                  <TableCell>
                    <strong>IP</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.map((registro) => {
                  const accionConfig = getAccionConfig(registro.accion);
                  return (
                    <TableRow key={registro.id} hover>
                      <TableCell>
                        {formatearFecha(registro.fechaAcceso)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {capitalizarPalabras(
                            `${registro.empleado?.nombre} ${registro.empleado?.apellidos}`,
                          ) || "Desconocido"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatearRutDisplay(registro.empleadoAcceso)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={registro.rolEmpleado}
                          size="small"
                          color={
                            registro.rolEmpleado === "SUPER_ADMIN"
                              ? "primary"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={accionConfig.label}
                          size="small"
                          color={accionConfig.color}
                          className={classes.accionChip}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          style={{ fontFamily: "monospace" }}
                        >
                          {registro.ipAddress || "N/A"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalHistorialAuditoriaClaves;
